import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ErrorOutline, WifiOffOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useGetNonCompliantPatientsQuery } from "app/store/patient/api/patientApi";
import {
  changePatient,
  toggleModal,
} from "app/store/transmission/slice/transmissionSlice";
import { REMINDER_ATTEMPTS_MAPPER, REMINDER_MAPPER } from "static/constants";
import {
  formatDateTime,
  getDateDifference,
  getDiffDaysFromCurrentDate,
} from "utils/date";
import { textFilter } from "utils/table";
import { truncate } from "utils/string";
import ReminderAttemptStatus from "components/common/reminder_attempt_status/ReminderAttemptStatus";

const useNonCompliantPatientsTable = () => {
  const filterIds = [
    "first_call",
    "second_call",
    "third_call",
    "letter",
    "live_call",
  ];

  const reminderFilters = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Sent",
      value: "sent",
    },
    {
      label: "Failed",
      value: "failed",
    },
    {
      label: "In-progress",
      value: "in-progress",
    },
    {
      label: "No Subscription",
      value: "noSubscription",
    },
    {
      label: "—",
      value: "subscribed",
    },
  ];

  const liveCallFilters = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Received",
      value: "received",
    },
    {
      label: "Failed",
      value: "failed",
    },
  ];

  const { year, month, noShowType } = useSelector(
    (state) => state.nonCompliantPatient
  );

  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [containerWidth, setContainerWidth] = useState("100%");
  const [columnFilters, setColumnFilters] = useState(
    filterIds.map((id) => ({ id, value: "all" }))
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const { data, isLoading, isFetching, isError } =
    useGetNonCompliantPatientsQuery(
      {
        year,
        month,
        noShowType,
        page: pagination?.pageIndex + 1,
        pageSize: pagination?.pageSize,
        filters: columnFilters,
        sorting,
      },
      { refetchOnMountOrArgChange: true }
    );

  useLayoutEffect(() => {
    if (tableContainerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        setContainerWidth(`${tableContainerRef.current.offsetWidth}px`);
      });
      resizeObserver.observe(tableContainerRef.current);

      setContainerWidth(`${tableContainerRef.current.offsetWidth}px`);

      return () => resizeObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    setExpanded({});
  }, [isLoading, isFetching]);

  const handlePatientProfile = (rowData) => {
    dispatch(
      changePatient({
        patientId: rowData?._id,
        serialNumber: rowData?.serial_number,
        account: rowData?.account,
      })
    );
    dispatch(toggleModal({ type: "patient", value: true }));
  };

  const columns = [
    {
      header: "Evoke ID",
      accessorKey: "evoke_id",
      filterFn: textFilter,
      enableSorting: false,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return `#${rowData?.evoke_id}`;
      },
    },
    {
      header: "Name",
      accessorKey: "name",
      filterFn: textFilter,
      size: 250,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const isOverdue = rowData?.late_date
          ? getDiffDaysFromCurrentDate(rowData?.late_date) > 41
          : false;

        return (
          <div
            className="d-flex transmission_patient_column"
            onClick={(e) => {
              e.stopPropagation();
              handlePatientProfile(rowData);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <>
              <Tooltip title={rowData?.name}>
                <span>{truncate(rowData?.name, 18)}</span>
                {rowData?.is_monitor_disconnected && (
                  <IconButton
                    sx={{
                      backgroundColor: "#FDEDED !important",
                      padding: "4px",
                      marginLeft: "15px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WifiOffOutlined
                      className="icon"
                      color="error"
                      fontSize="small"
                    />
                  </IconButton>
                )}
              </Tooltip>

              {isOverdue && (
                <Tooltip title="Overdue Patient">
                  <IconButton
                    sx={{
                      backgroundColor: "#FDEDED !important",
                      padding: "4px",
                      marginLeft: "15px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ErrorOutline
                      className="icon"
                      color="error"
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </>
          </div>
        );
      },
    },
    {
      header: "Account",
      accessorKey: "account",
      filterFn: textFilter,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return <p>{rowData?.account}</p>;
      },
    },
    {
      header: "Manufacturer",
      accessorKey: "manufacturer",
      filterFn: textFilter,
      enableSorting: false,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return <p>{rowData?.manufacturer}</p>;
      },
    },
    {
      header: "Late Date",
      accessorKey: "late_date",
      filterVariant: "date",
      filterFn: textFilter,
      enableSorting: false,
      Cell: ({ row }) => {
        const lateDate = row?.original?.late_date;
        const diffInDays = lateDate
          ? getDateDifference({
              start: lateDate,
              end: new Date(),
              unit: "days",
              precise: false,
            })
          : "";

        return (
          <div>
            <p>{lateDate ? formatDateTime(lateDate) : "—"}</p>
            {diffInDays >= 1 && (
              <p style={{ color: "red", fontSize: "0.7rem" }}>
                {diffInDays} days late
              </p>
            )}
          </div>
        );
      },
    },
    {
      header: "Disconnected At",
      accessorKey: "disconnected_at",
      filterVariant: "date",
      filterFn: textFilter,
      enableSorting: false,
      Cell: ({ row }) => {
        const disconnectedAt =
          row?.original?.deviceInformation?.monitor_disconnected_at;

        const diffInDays = disconnectedAt
          ? getDateDifference({
              start: disconnectedAt,
              end: new Date(),
              unit: "days",
              precise: false,
            })
          : "";

        return (
          <div>
            <p>{disconnectedAt ? formatDateTime(disconnectedAt) : "—"}</p>
            {diffInDays >= 1 && (
              <p style={{ color: "red", fontSize: "0.7rem" }}>
                {diffInDays} days disconnected
              </p>
            )}
          </div>
        );
      },
    },
    ...REMINDER_MAPPER.map((data) => ({
      header: data?.value,
      accessorKey: `${data?.key}`,
      filterFn: textFilter,
      enableSorting: false,
      filterVariant: "select",
      filterSelectOptions:
        data?.key === "live_call" ? liveCallFilters : reminderFilters,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const reminders = rowData?.reminders || [];
        const reminderAttemptKey = REMINDER_ATTEMPTS_MAPPER?.[data?.key];

        const reminderAttempts = reminders?.[reminderAttemptKey] || {};

        const isReminderSubscribed = rowData?.subscribe_setting?.subscribe;
        const subscribeSetting = rowData?.subscribe_setting?.[data?.key];
        const liveCallReminder = rowData?.live_call_reminder;

        return (
          <div className="d-flex g-1 flex-column">
            <div>
              <ReminderAttemptStatus
                reminderKey={data?.key}
                reminderAttempts={reminderAttempts}
                isReminderSubscribed={isReminderSubscribed}
                subscribeSetting={subscribeSetting}
                liveCallReminder={liveCallReminder}
              />
            </div>
          </div>
        );
      },
    })),
  ];

  return {
    data,
    columns,
    isError,
    sorting,
    expanded,
    isLoading,
    isFetching,
    pagination,
    columnFilters,
    containerWidth,
    tableContainerRef,
    dispatch,
    setSorting,
    setExpanded,
    setPagination,
    setColumnFilters,
    handlePatientProfile,
  };
};

export default useNonCompliantPatientsTable;
